import { CLAIM_STATUS, defaultFiltersValue, PENDING_STATUSES } from '@/data';
import { IFilter } from '@/types';
import { reactive, watch } from 'vue';
import { LocationQuery, LocationQueryValue } from 'vue-router';

export const FILTER_KEY = 'FILTER_KEY';

/**
 * Manage state of filters throughout application.  This is a first pass at extracting
 * filter logic from the Home.vue and Filter.vue components.
 */
export class Filter {
  /**
   * Get an object to help manage filter state.
   */
  public static useFilter(routeQuery?: LocationQuery): Filter {
    const newFilter = new Filter();
    if (routeQuery) {
      newFilter._setFromLocationQuery(routeQuery);
    }
    return newFilter;
  }

  private _filterState: IFilter = reactive<IFilter>({
    status: defaultFiltersValue.status, // default value for status is required
    page: defaultFiltersValue.page, // default value for page is required
    size: defaultFiltersValue.size, // default value for size is required
    claimCategory: '',
    claimIssueCategory: '',
    claimIssueType: '',
    resolutionType: '',
    tag: '',
    claimPriority: '',
    claimDefectSeverity: '',
    supplier: '',
    lotNo: '',
    productName: '',
    boxId: '',
    skuNo: '',
    searchText: '',
    // isUpdatedByCurrentUser: 'false',
    dateDeliveredBegin: '',
    dateDeliveredEnd: '',
    batchCode: '',
    isBatchClosed: '',
    isClaimFlagged: '',
    isProductQualityIssue: '',
  });

  /**
   * Update the filter based on the values provided in a router querystring
   */
  private _setFromLocationQuery(routeQuery: LocationQuery): void {
    Object.keys(routeQuery).forEach((key: string) => {
      if (this.filter[key as keyof IFilter] !== undefined) {
        this.filter[key as keyof IFilter] = routeQuery[key as keyof IFilter] as string;
      }
    });
  }

  /**
   * Use public static method `Filter.useFilter()` to instantiate.
   */
  private constructor() {
    watch(
      () => this._filterState.supplier,
      () => {
        this._filterState.batchCode = '';
        if (this.isStatusPending) {
          this._filterState.status = CLAIM_STATUS.QA_PENDING_ALL;
        }
      }
    );

    watch(
      () => [this._filterState.status, this._filterState.batchCode],
      () => {
        this._filterState.page = defaultFiltersValue.page;
      }
    );

    watch(
      () => this.isStatusPending,
      () => {
        this._filterState.isBatchClosed = this.isStatusPending ? 'false' : '';
        this._filterState.batchCode = '';
      }
    );
  }

  /**
   * Update a field in the filters to some new value
   * @param fieldName Field name to update
   * @param value Value to store in filter
   */
  public updateFilter(fieldName: keyof IFilter, value: string) {
    this._filterState[fieldName] = value;
  }

  /**
   * Get the whole filter state
   */
  public get filter(): IFilter {
    return this._filterState;
  }

  public get isStatusPending(): boolean {
    return PENDING_STATUSES.includes(this._filterState.status);
  }

  /**
   * Get a LocationQuery (router querystring) value based on the filter query provided
   */
  public toLocationQuery(): LocationQuery {
    const newRouteQuery: LocationQuery = {};
    Object.keys(this._filterState).forEach((key: string) => {
      const queryKey: keyof IFilter = key as keyof IFilter;
      if (this._filterState[queryKey] && this._filterState[queryKey] !== '') {
        newRouteQuery[key] = this._filterState[queryKey] as LocationQueryValue;
      }
    });
    return newRouteQuery;
  }
}
